import React, { useImperativeHandle, useState, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  AccountTree as AccountTreeIcon,
  LibraryBooks as LibraryBooksIcon,
  SearchOutlined,
  AdminPanelSettings as AdminPanelSettingsIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectGroups } from "../../features/auth/authSlice";
import {
  Sidebar,
  Menu,
  MenuItem,
  useProSidebar,
  sidebarClasses,
} from "react-pro-sidebar";
import MenuIcon from "@mui/icons-material/Menu";
import RWELogo from "../../assets/images/RWE-Logo-Black.jpg";

// import 'react-pro-sidebar/dist/css/styles.css';
import { borderRadius } from "../../constants";
import ICD9CodesModal from "../ICD9CodesModal";
import ICD10CodesModal from "../ICD10CodesModal";
import CPTCodesModal from "../querybuilder/CPTCodesModal";

// Constants
const FONT_SIZE = "0.87rem";
const PRIMARY_COLOR = "#04aeea";
// The rail is a dark brand surface in both modes, so its own chrome is defined
// against that surface rather than against the page palette. Hairlines are
// translucent white so they read as separation, never as a bright line.
const SIDEBAR_BG = "#000000";
const SIDEBAR_TEXT = "rgba(255, 255, 255, 0.92)";
const SIDEBAR_TEXT_MUTED = "rgba(255, 255, 255, 0.55)";
const SIDEBAR_HAIRLINE = "rgba(255, 255, 255, 0.08)";
const SIDEBAR_HOVER = "rgba(255, 255, 255, 0.08)";
const MENU_ITEMS = [
  { name: "Dashboard", path: "/", icon: DashboardIcon },
  { name: "Query Builder", path: "/queryBuilder", icon: AccountTreeIcon },
  { name: "Saved Queries", path: "/savedqueries", icon: LibraryBooksIcon },
  { name: "RWE AI", path: "/rwe-ai", icon: AutoAwesomeIcon, adminsOnly: true },
  { name: "Admin Portal", path: "/admin", icon: AdminPanelSettingsIcon, adminOnly: true },
];

const SidebarMenu = React.forwardRef(({ setActiveStep, activeStep, metadata }, ref) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { collapseSidebar } = useProSidebar();
  const groups = useSelector(selectGroups);

  const [selected, setSelected] = useState("Dashboard");
  const [confirmNewQueryOpen, setConfirmNewQueryOpen] = useState(false);
  const [openModal, setOpenModal] = useState({
    cpt: false,
    icd9: false,
    icd10: false,
  });

  const pathToMenuName = useMemo(() => ({
    '/': 'Dashboard',
    '/queryBuilder': 'Query Builder',
    '/savedqueries': 'Saved Queries',
    '/rwe-ai': 'RWE AI',
    '/admin': 'Admin Portal',
  }), []);

  useEffect(() => {
    let menuName = 'Dashboard';

    if (pathToMenuName[pathname]) {
      menuName = pathToMenuName[pathname];
    } else if (pathname.startsWith('/queryBuilder')) {
      menuName = 'Query Builder';
    } else if (pathname.startsWith('/savedqueries')) {
      menuName = 'Saved Queries';
    }

    setSelected(menuName);
  }, [pathname]);

  const handleMenuSelection = (name, options = {}) => {
    const { resetQuery = true } = options;

    if (resetQuery) {
      setActiveStep(0);
    }
    setSelected(name);
  };

  useImperativeHandle(ref, () => ({
    handleMenuSelection(name, options) {
      handleMenuSelection(name, options);
    },
  }));

  const handleConfirmNewQuery = () => {
    setConfirmNewQueryOpen(false);
    handleMenuSelection("Query Builder");
    if (pathname !== "/queryBuilder") {
      navigate("/queryBuilder");
    }
  };

  const handleCancelNewQuery = () => {
    setConfirmNewQueryOpen(false);
  };

  const renderMenuItem = (item) => (
    <MenuItem
      key={item.name}
      active={item.name === selected}
      icon={<item.icon fontSize={FONT_SIZE} />}
      component={<Link to={item.path} />}
      onClick={(event) => {
        const isQueryBuilder = item.name === "Query Builder";
        const isQueryBuilderRoute = pathname.startsWith("/queryBuilder");
        if (isQueryBuilder && isQueryBuilderRoute && activeStep > 0) {
          event.preventDefault();
          handleMenuSelection(item.name, { resetQuery: false });
          setConfirmNewQueryOpen(true);
          return;
        }

        handleMenuSelection(item.name);
      }}
    >
      <Box display={"flex"}>
        <Typography
          fontSize={FONT_SIZE}
          sx={{
            color: item.name === selected ? "#04121a" : SIDEBAR_TEXT
          }}
        >
          {item.name}
        </Typography>
      </Box>
    </MenuItem>
  );

  const displayCodes = () => {
    if (activeStep === 1) {
      return (
        <>
          <Typography
            variant="h6"
            sx={{
              m: "30px 0 5px 10px",
              fontSize: "1rem",
              color: SIDEBAR_TEXT_MUTED,
              letterSpacing: "0.04em",
            }}
          >
            Codes
          </Typography>
          <MenuItem
            icon={<SearchOutlined fontSize={FONT_SIZE} />}
            onClick={() => {
              setOpenModal({ ...openModal, icd9: true });
            }}
          >
            <Box display={"flex"}>
              <Typography
                fontSize={FONT_SIZE}
                sx={{ color: SIDEBAR_TEXT }}
              >
                ICD 9
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem
            icon={<SearchOutlined fontSize={FONT_SIZE} />}
            onClick={() => {
              setOpenModal({ ...openModal, icd10: true });
            }}
          >
            <Box display={"flex"}>
              <Typography
                fontSize={FONT_SIZE}
                sx={{ color: SIDEBAR_TEXT }}
              >ICD 10</Typography>
            </Box>
          </MenuItem>
          <MenuItem
            icon={<SearchOutlined fontSize={FONT_SIZE} />}
            onClick={() => {
              setOpenModal({ ...openModal, cpt: true });
            }}
          >
            <Box display={"flex"}>
              <Typography
                fontSize={FONT_SIZE}
                sx={{ color: SIDEBAR_TEXT }}
              >CPT</Typography>
            </Box>
          </MenuItem>
          <ICD9CodesModal
            openModal={openModal}
            setOpenModal={setOpenModal}
            metadata={metadata}
          />
          <ICD10CodesModal
            openModal={openModal}
            setOpenModal={setOpenModal}
            metadata={metadata}
          />
          <CPTCodesModal
            openModal={openModal}
            setOpenModal={setOpenModal}
            metadata={metadata}
          />
        </>
      );
    }
    return null;
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <Sidebar
        transitionDuration={400}
        rootStyles={{
          border: "none",
          color: SIDEBAR_TEXT,
          [`.${sidebarClasses.container}`]: {
            backgroundColor: SIDEBAR_BG,
            borderRight: "none",
          },
        }}
        width="200px"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            height: "57px",
            paddingLeft: 4,
            backgroundColor: SIDEBAR_BG,
            borderBottom: `1px solid ${SIDEBAR_HAIRLINE}`,
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => collapseSidebar()}
            edge="start"
            disableRipple
            sx={{
              bgcolor: "transparent",
              color: SIDEBAR_TEXT,
              border: `1px solid ${SIDEBAR_HAIRLINE}`,
              borderRadius: borderRadius,
              transition: "background-color 0.2s ease, color 0.2s ease",
              "&:hover": {
                bgcolor: SIDEBAR_HOVER,
                color: PRIMARY_COLOR,
              },
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        </Box>
        <Menu
          defaultValue={"Dashboard"}
          menuItemStyles={{
            button: ({ level, active, disabled, hover }) => {
              return {
                color: active ? "#04121a" : SIDEBAR_TEXT,
                backgroundColor: active ? PRIMARY_COLOR : "transparent",
                borderRadius: '12px',
                margin: '4px 12px',
                padding: '12px 16px',
                border: 'none',
                fontWeight: active ? 600 : 500,
                transition: 'all 0.2s ease-in-out',
                "&:hover": active
                  ? {
                    backgroundColor: theme.palette.primary.dark,
                    color: '#04121a',
                    transform: 'translateX(4px)',
                    boxShadow: '0 4px 12px rgba(4, 174, 234, 0.25)'
                  }
                  : {
                    backgroundColor: SIDEBAR_HOVER,
                    color: SIDEBAR_TEXT,
                    transform: 'translateX(4px)',
                    borderRadius: '12px'
                  },
              };
            },
          }}
        >
          {MENU_ITEMS.filter(item => {
            if (item.adminsOnly) return groups.includes('ADMINS');
            if (item.adminOnly) return groups.length > 0;
            return true;
          }).map(renderMenuItem)}

          {displayCodes()}
        </Menu>
      </Sidebar>
      <Box
        sx={{
          position: "fixed",
          left: "-85px",
          bottom: "10%",
          zIndex: 10,
        }}
      >
        <img
          src={RWELogo}
          alt="logo"
          style={{
            transform: "rotate(270deg)",
            width: "15rem",
            height: "5rem",
          }}
        />
      </Box>
      <Dialog
        open={confirmNewQueryOpen}
        onClose={handleCancelNewQuery}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Start a new query?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your current query will be cleared if you start a new one. You can
            keep working or start fresh.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNewQuery}>
            Keep current query
          </Button>
          <Button
            onClick={handleConfirmNewQuery}
            color="error"
            variant="contained"
          >
            Start new query
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

SidebarMenu.displayName = "SidebarMenu";

export default SidebarMenu;
